import { FC, ReactNode } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  FileCheck,
  CreditCard,
} from 'lucide-react';
import { cn } from '../../../utils/cn';

interface SummaryProps {
  summary: {
    totalPayments: number;
    matchedCount: number;
    unmatchedCount: number;
    discrepancyCount: number;
    resolvedCount: number;
    pendingResolutionCount: number;
  };
}

interface CardData {
  label: string;
  value: number;
  icon: ReactNode;
  bgColor: string;
  iconColor: string;
}

export const ReconciliationSummaryCards: FC<SummaryProps> = ({ summary }) => {
  const cards: CardData[] = [
    {
      label: 'Total Payments',
      value: summary.totalPayments,
      icon: <CreditCard className="h-6 w-6" />,
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      label: 'Matched',
      value: summary.matchedCount,
      icon: <CheckCircle className="h-6 w-6" />,
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      iconColor: 'text-green-600 dark:text-green-400',
    },
    {
      label: 'Discrepancies',
      value: summary.discrepancyCount,
      icon: <AlertTriangle className="h-6 w-6" />,
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      iconColor: 'text-amber-600 dark:text-amber-400',
    },
    {
      label: 'Unmatched',
      value: summary.unmatchedCount,
      icon: <XCircle className="h-6 w-6" />,
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      iconColor: 'text-red-600 dark:text-red-400',
    },
    {
      label: 'Resolved',
      value: summary.resolvedCount,
      icon: <FileCheck className="h-6 w-6" />,
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      iconColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      label: 'Pending',
      value: summary.pendingResolutionCount,
      icon: <Clock className="h-6 w-6" />,
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      iconColor: 'text-orange-600 dark:text-orange-400',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
      {cards.map((card) => (
        <div
          key={card.label}
          data-testid="stat-card"
          className={cn(
            'rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm'
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{card.value}</p>
              <p className="mt-1 truncate text-sm text-gray-500 dark:text-slate-400">{card.label}</p>
            </div>
            <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg', card.bgColor)}>
              <span className={card.iconColor}>{card.icon}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
